import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'wouter';
import { useGetChatRoom } from '@workspace/api-client-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { LangToggle } from '@/components/ui/LangToggle';
import { Copy, LockKeyhole, LogOut, Loader2, Share2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ParticipantsSidebar } from '@/components/chat/ParticipantsSidebar';
import { MobileParticipantsSheet } from '@/components/chat/MobileParticipantsSheet';
import { ChatWindow } from '@/components/chat/ChatWindow';
import { useChat } from '@/hooks/useChat';
import { useLang } from '@/lib/i18n';
import {
  getRoomSecret,
  withRoomSecret,
  isValidRoomSecret,
  rememberRoomSecret,
} from '@/lib/roomCrypto';

export function RoomPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { t } = useLang();

  // The pseudo is intentionally NEVER persisted.
  // Every opening of a room starts with an empty name
  // and requires the user to enter it again.
  const [name, setName] = useState<string>('');
  const [showNameDialog, setShowNameDialog] = useState<boolean>(true);

  const [tempName, setTempName] = useState('');
  const [dmTarget, setDmTarget] = useState('');
  const [showMobileParticipants, setShowMobileParticipants] =
    useState(false);

  const [isLeaving, setIsLeaving] = useState(false);

  const {
    data: room,
    isError,
    isLoading,
  } = useGetChatRoom(roomId || '', {
    query: {
      enabled: !!roomId,
      retry: false,
      queryKey: ['chatRoom', roomId || ''] as const,
    },
  });

  const roomSecret = getRoomSecret(roomId);

  /*
   * Restore the E2EE key in the URL fragment when the room
   * was opened without the fragment but the secret is still
   * available locally.
   *
   * This does NOT create or close a room.
   */
  useEffect(() => {
    if (!roomId || !roomSecret) return;

    rememberRoomSecret(
      roomId,
      roomSecret,
    );

    if (!window.location.hash) {
      window.history.replaceState(
        window.history.state,
        '',
        withRoomSecret(
          `/room/${encodeURIComponent(
            roomId,
          )}`,
          roomSecret,
        ),
      );
    }
  }, [roomId, roomSecret]);

  /*
   * IMPORTANT:
   *
   * activeRoomId is only supplied to useChat when the
   * secure room link and the username are both valid.
   *
   * Android backgrounding, visibility changes and network
   * interruptions never call leaveRoom().
   */
  const activeRoomId =
    name &&
    roomId &&
    isValidRoomSecret(roomSecret)
      ? roomId
      : '';

  const activeName = name;

  const chatContext = useChat(
    activeRoomId,
    activeName,
  );

  useEffect(() => {
    if (!isError) return;

    toast({
      title: t('room.notFound'),
      description: t(
        'room.notFoundDesc',
      ),
      variant: 'destructive',
    });

    setLocation('/');
  }, [
    isError,
    setLocation,
    toast,
    t,
  ]);

  const handleSaveName = (
    e: React.FormEvent,
  ) => {
    e.preventDefault();

    const finalName = tempName.trim();

    if (!finalName) return;

    // Deliberately do not store the pseudo anywhere.
    // It exists only in React memory for this room opening.
    setName(finalName);
    setShowNameDialog(false);
  };

  const handleOpenContacts = useCallback(() => {
    if (typeof window === 'undefined' || !roomId) return;

    // Keep the room E2EE secret in the fragment while opening the
    // local encrypted contacts vault. The fragment is never sent
    // to the server and ContactsPage uses it to return to the room.
    const secret = getRoomSecret(roomId);
    const hash = secret ? `#k=${encodeURIComponent(secret)}` : window.location.hash;
    window.location.assign(`/contacts?room=${encodeURIComponent(roomId)}${hash}`);
  }, [roomId]);

  const getRoomShareUrl = useCallback(() => {
    if (typeof window === 'undefined' || !roomId) {
      return null;
    }

    const secret = getRoomSecret(roomId);

    if (!secret) {
      throw new Error(
        'Missing end-to-end encryption key in room link',
      );
    }

    return new URL(
      withRoomSecret(
        `/room/${encodeURIComponent(roomId)}`,
        secret,
      ),
      window.location.origin,
    ).toString();
  }, [roomId]);

  const handleCopyLink = useCallback(
    async () => {
      try {
        const text = getRoomShareUrl();

        if (!text) {
          return;
        }

        if (
          navigator.clipboard &&
          typeof navigator.clipboard.writeText === 'function'
        ) {
          await navigator.clipboard.writeText(text);
        } else {
          const textarea = document.createElement('textarea');

          textarea.value = text;
          textarea.style.position = 'fixed';
          textarea.style.left = '-9999px';

          document.body.appendChild(textarea);

          try {
            textarea.focus();
            textarea.select();

            const copied = document.execCommand('copy');

            if (!copied) {
              throw new Error('Copy failed');
            }
          } finally {
            document.body.removeChild(textarea);
          }
        }

        toast({
          description: t('room.linkCopied'),
          duration: 3000,
        });
      } catch (error) {
        console.error('Failed to copy room link:', error);

        const fallbackErrorMsg = t('room.copyFailed');

        const displayMessage =
          fallbackErrorMsg === 'room.copyFailed'
            ? 'Impossible de copier le lien.'
            : fallbackErrorMsg;

        toast({
          title: t('common.error'),
          description: displayMessage,
          variant: 'destructive',
        });
      }
    },
    [getRoomShareUrl, toast, t],
  );

  /*
   * Mobile/Android native sharing.
   *
   * navigator.share hands the complete ROOM URL, including the #k=
   * E2EE fragment, to the Android share sheet. Android then offers
   * installed targets such as WhatsApp, Telegram, Messages/SMS,
   * Messenger, etc.
   *
   * If native sharing is unavailable, fall back to copying the link.
   */
  const handleShareLink = useCallback(
    async () => {
      try {
        const text = getRoomShareUrl();

        if (!text) {
          return;
        }

        if (
          typeof navigator !== 'undefined' &&
          typeof navigator.share === 'function'
        ) {
          await navigator.share({
            title: 'ROOM',
            text: 'Rejoins-moi sur ROOM',
            url: text,
          });

          return;
        }

        await handleCopyLink();
      } catch (error) {
        /*
         * AbortError means the user simply closed the Android share
         * sheet. It is not an application error.
         */
        if (
          error instanceof DOMException &&
          error.name === 'AbortError'
        ) {
          return;
        }

        console.error('Failed to share room link:', error);

        try {
          await handleCopyLink();
        } catch {
          toast({
            title: t('common.error'),
            description: 'Impossible de partager le lien.',
            variant: 'destructive',
          });
        }
      }
    },
    [getRoomShareUrl, handleCopyLink, toast, t],
  );

  /*
   * EXPLICIT VOLUNTARY ROOM CLOSE
   *
   * This is the ONLY UI path that permanently closes
   * the room.
   *
   * useChat.leaveRoom():
   *
   *   1. stops reconnection
   *   2. closes the current socket
   *   3. calls Gateway /close
   *   4. waits for HTTP confirmation
   *   5. only then removes local credentials
   *
   * If the Gateway is unreachable, leaveRoom throws.
   * We stay on the room page and DO NOT lose the local
   * resume credentials.
   */
  const handleLeave = useCallback(
    async () => {
      if (
        isLeaving ||
        !activeRoomId
      ) {
        return;
      }

      setIsLeaving(true);

      try {
        await chatContext.leaveRoom();

        /*
         * The Gateway has confirmed permanent
         * room destruction.
         *
         * Only now is navigation allowed.
         */
        setLocation('/');
      } catch (error) {
        console.error(
          '[ROOM] Unable to leave room permanently:',
          error,
        );

        const fallbackMessage =
          'Impossible de fermer définitivement la room. Vérifie la connexion au serveur et réessaie.';

        toast({
          title:
            'Fermeture impossible',
          description:
            error instanceof Error &&
            error.message
              ? fallbackMessage
              : fallbackMessage,
          variant:
            'destructive',
          duration: 5000,
        });

        /*
         * Stay on the room.
         *
         * The local resume credentials have NOT been
         * deleted by useChat because the Gateway did
         * not confirm the close.
         */
      } finally {
        setIsLeaving(false);
      }
    },
    [
      isLeaving,
      activeRoomId,
      chatContext.leaveRoom,
      setLocation,
      toast,
    ],
  );

  const handleClearDm =
    useCallback(() => {
      setDmTarget('');
    }, []);

  if (isLoading) {
    return (
      <div className="min-h-[100dvh] w-full flex items-center justify-center bg-background text-muted-foreground px-4">
        {t('room.loading')}
      </div>
    );
  }

  if (isError) {
    return null;
  }

  if (
    !isValidRoomSecret(
      roomSecret,
    )
  ) {
    return (
      <div className="min-h-[100dvh] w-full flex items-center justify-center bg-background px-4 text-foreground">
        <div className="w-full max-w-md rounded-2xl border border-border/60 bg-card/70 p-6 text-center shadow-xl backdrop-blur-xl">
          <div className="text-lg font-medium mb-2">
            Lien sécurisé requis
          </div>

          <p className="text-sm text-muted-foreground mb-5">
            ROOM utilise une clé présente
            uniquement dans le lien
            d’invitation pour chiffrer les
            messages de bout en bout.
          </p>

          <Button
            onClick={() =>
              setLocation('/')
            }
            className="w-full"
          >
            Retour à ROOM
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] min-h-0 w-full flex flex-col bg-background overflow-hidden font-sans">
      {/* Header */}
      <header className="min-h-14 flex items-center justify-between px-3 sm:px-4 pt-[max(0.25rem,env(safe-area-inset-top))] pb-2 bg-card/60 backdrop-blur-xl border-b border-border shadow-sm shrink-0 z-20">
        <div className="flex items-center gap-2 sm:gap-3 md:gap-4 min-w-0">
          {/* Desktop room pill */}
          <div className="hidden md:flex items-center gap-2 bg-muted/30 px-3 py-1.5 rounded-full border border-border/40 backdrop-blur-md">
            <span className="text-xs font-mono text-muted-foreground">
              room:
            </span>

            <span className="text-sm font-medium max-w-[80px] sm:max-w-[120px] truncate">
              {roomId}
            </span>
          </div>

          {/* Mobile room pill */}
          <button
            type="button"
            onClick={() =>
              setShowMobileParticipants(
                true,
              )
            }
            className="flex md:hidden items-center gap-2 bg-muted/30 px-3 py-1.5 rounded-full border border-border/40 backdrop-blur-md active:scale-95 transition-transform"
          >
            <span className="text-xs font-mono text-muted-foreground">
              room:
            </span>

            <span className="text-sm font-medium max-w-[80px] truncate">
              {roomId}
            </span>
          </button>
        </div>

        <div className="flex items-center gap-1 sm:gap-2">
          {/* Contacts / encrypted local vault */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleOpenContacts}
            disabled={isLeaving}
            className="min-h-10 px-2 sm:px-3 text-muted-foreground hover:text-foreground transition-colors hidden sm:flex"
            title="Ouvrir le coffre-fort Contacts"
          >
            <LockKeyhole className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Contacts</span>
          </Button>

          {/* Desktop copy */}
          <Button
            variant="ghost"
            size="sm"
            onClick={
              handleCopyLink
            }
            disabled={isLeaving}
            className="min-h-10 px-2 sm:px-3 text-muted-foreground hover:text-foreground hidden sm:flex transition-colors"
          >
            <Copy className="h-4 w-4 sm:mr-2" />

            <span className="hidden sm:inline">
              {t('room.copyLink')}
            </span>
          </Button>

          {/* Mobile copy */}
          <Button
            variant="ghost"
            size="icon"
            onClick={
              handleCopyLink
            }
            disabled={isLeaving}
            className="h-10 w-10 sm:hidden text-muted-foreground"
          >
            <Copy className="h-4 w-4" />
          </Button>

          <div className="w-px h-5 bg-border mx-1" />

          <LangToggle />

          <ThemeToggle />

          {/* Explicit permanent close */}
          <Button
            variant="destructive"
            size="sm"
            onClick={
              handleLeave
            }
            disabled={
              isLeaving
            }
            className="min-h-10 ml-1"
          >
            {isLeaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin sm:mr-2" />

                <span className="hidden sm:inline">
                  Fermeture...
                </span>
              </>
            ) : (
              <>
                <span className="hidden sm:inline">
                  {t('room.leave')}
                </span>

                <LogOut className="h-4 w-4 sm:hidden" />
              </>
            )}
          </Button>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex-1 min-h-0 flex overflow-hidden relative">
        <ParticipantsSidebar
          participants={
            chatContext.participants
          }
          count={
            chatContext.participantCount ??
            room?.participant_count ??
            0
          }
          myName={name}
          dmTarget={dmTarget}
          onSelectDm={
            setDmTarget
          }
        />

        <MobileParticipantsSheet
          open={
            showMobileParticipants
          }
          onOpenChange={
            setShowMobileParticipants
          }
          participants={
            chatContext.participants
          }
          count={
            chatContext.participantCount ??
            room?.participant_count ??
            0
          }
          myName={name}
          dmTarget={dmTarget}
          onSelectDm={(target) => {
            setDmTarget(target);

            setShowMobileParticipants(
              false,
            );
          }}
          onInvite={handleOpenContacts}
          onShareLink={handleShareLink}
          onCopyLink={handleCopyLink}
          roomIsFull={(chatContext.participantCount ?? room?.participant_count ?? 0) >= 8}
          maxUsers={8}
        />

        {!showNameDialog &&
        roomId ? (
          <div className="flex-1 min-h-0 flex flex-col min-w-0 overflow-hidden">
            <ChatWindow
              {...chatContext}
              name={name}
              dmTarget={dmTarget}
              onClearDm={
                handleClearDm
              }
            />
          </div>
        ) : (
          <div className="flex-1 bg-background" />
        )}
      </div>

      {/* Name Gate Modal */}
      <Dialog
        open={showNameDialog}
        onOpenChange={() => {}}
      >
        <DialogContent
          className="sm:max-w-md backdrop-blur-2xl bg-card border-border shadow-2xl"
          hideCloseButton
        >
          <DialogHeader className="pt-2">
            <DialogTitle className="text-2xl font-light text-center mb-1">
              {t(
                'room.enterName',
              )}
            </DialogTitle>

            <DialogDescription className="text-center text-muted-foreground">
              {t(
                'room.needName',
              )}
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={
              handleSaveName
            }
            className="space-y-6 py-4"
          >
            <div className="space-y-2">
              <Input
                value={tempName}
                onChange={(e) =>
                  setTempName(
                    e.target.value,
                  )
                }
                placeholder={t(
                  'home.namePlaceholder',
                )}
                className="min-h-12 bg-background/50 text-center text-lg"
                autoFocus
                autoComplete="off"
                maxLength={30}
              />
            </div>

            <Button
              type="submit"
              className="w-full min-h-12 text-md"
              disabled={
                !tempName.trim()
              }
            >
              {t(
                'room.joinRoom',
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
